import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { billsApi } from '@/api/bills-api'
import { useToast } from '@/hooks/use-toast'
import type { Bill, BillWorkflowStatus, BillAction } from '@/types/bill'
import type { APISession } from '@/types/session'
import { isValidAPISession } from '@/types/session'
import { getJob, configureJobsAuth } from '@umd-dit/jobs-component-react'
import { addAuthHeaders } from '@umd-dit/oidc-core'
import { JobStatus } from '@/types/job'

// Define sorting related types
export type SortField = 'impact' | 'billNumber' | 'title' | 'lastModified' | 'crossfileBillNumber' | null
export type SortDirection = 'asc' | 'desc'

interface BillsWorkflowReturn {
  bills: Bill[]
  isLoading: boolean
  isError: boolean
  error: string | null
  selectedBills: string[]
  activeTab: BillWorkflowStatus
  selectedSession: APISession | null
  setSelectedSession: (session: APISession) => void
  setActiveTab: (tab: BillWorkflowStatus) => void
  handleSelectBill: (billId: string) => void
  handleSelectAll: (bills: Bill[]) => void
  updateBillsWorkflow: (billIds: string[], newStatus: BillWorkflowStatus) => Promise<number>
  processBillAction: (billIds: string[], action: BillAction) => Promise<number>
  currentPage: number
  pageSize: number
  totalPages: number
  handlePageChange: (page: number) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  totalFilteredItems: number
  isFetchingNextPage: boolean
  processingActions: Record<string, BillAction>
  sortField: SortField
  sortDirection: SortDirection
  setSorting: (field: SortField, direction: SortDirection) => void
}

export const useBillsWorkflow = (
  initialTab: BillWorkflowStatus = 'New',
  initialSession?: APISession
): BillsWorkflowReturn => {
  // Configure auth for jobs component
  useEffect(() => {
    configureJobsAuth(addAuthHeaders);
  }, []);

  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<BillWorkflowStatus>(initialTab)
  const [selectedBills, setSelectedBills] = useState<string[]>([])
  const [selectedSession, setSelectedSession] = useState<APISession | null>(
    initialSession && isValidAPISession(initialSession) ? initialSession : null
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [processingActions, setProcessingActions] = useState<Record<string, BillAction>>({})
  const [sortField, setSortField] = useState<SortField>('impact')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const pageSize = 20 // Fixed page size

  useEffect(() => {
    setSelectedBills([])
  }, [activeTab, selectedSession])

  useEffect(() => {
    if (initialSession && isValidAPISession(initialSession)) {
      console.log('🔄 Setting session from initialSession:', { initialSession })
      setSelectedSession(initialSession)
    }
  }, [initialSession])

  // Reset to first page when search query changes
  useEffect(() => {
    console.log(`Search query changed, resetting to page 1`);
    setCurrentPage(1);
  }, [searchQuery])

  // Function to handle sorting
  const setSorting = useCallback((field: SortField, direction: SortDirection) => {
    setSortField(field)
    setSortDirection(direction)
    setCurrentPage(1) // Reset to first page when sorting changes
  }, [])

  // Calculate impact value for sorting
  const getImpactValue = useCallback((bill: Bill): number => {
    const impact = bill.monitor_analysis?.summary?.overall_impact
    if (!impact) return -999 // No analysis should sort to the end
    
    // Create a numeric value for sorting that prioritizes type first, then score
    const typeValue = { positive: 1000, neutral: 0, negative: -1000 }
    return typeValue[impact.type] + impact.score
  }, [])

  // Sort bills based on current sort field and direction
  const sortBills = useCallback((bills: Bill[]): Bill[] => {
    if (!sortField) return bills

    return [...bills].sort((a, b) => {
      let comparison = 0

      if (sortField === 'impact') {
        const valueA = getImpactValue(a)
        const valueB = getImpactValue(b)
        comparison = valueB - valueA // Default descending for impact
      } else if (sortField === 'billNumber') {
        comparison = a.billNumber.localeCompare(b.billNumber)
      } else if (sortField === 'title') {
        comparison = (a.title || '').localeCompare(b.title || '')
      } else if (sortField === 'lastModified') {
        comparison = a.lastModified.localeCompare(b.lastModified)
      } else if (sortField === 'crossfileBillNumber') {
        // Handle null values for crossfileBillNumber
        if (!a.crossfileBillNumber && !b.crossfileBillNumber) {
          comparison = 0
        } else if (!a.crossfileBillNumber) {
          comparison = 1 // Sort nulls to the end
        } else if (!b.crossfileBillNumber) {
          comparison = -1 // Sort nulls to the end
        } else {
          comparison = a.crossfileBillNumber.localeCompare(b.crossfileBillNumber)
        }
      }

      // Apply sort direction
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [sortField, sortDirection, getImpactValue])

  const billsQuery = useInfiniteQuery({
    queryKey: ['bills', selectedSession, activeTab],
    queryFn: async ({ pageParam }) => {
      console.log('🔍 Query Function Called:', { 
        selectedSession,
        activeTab,
        pageParam,
        enabled: !!selectedSession
      })
      if (!selectedSession) {
        console.warn('⚠️ No session selected, query should be disabled')
        throw new Error('No session selected')
      }

      const response = await billsApi.listBills({
        session: selectedSession,
        workflowStatus: activeTab,
        nextToken: pageParam || undefined
      })

      return response
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextToken,
    enabled: !!selectedSession,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    gcTime: 3600000
  })

  // Automatically fetch pages until we have enough data for the current page
  // This should run only when currentPage changes and is stable
  const stableCurrentPage = useRef(currentPage);
  
  useEffect(() => {
    // Only execute when the page actually changes
    if (stableCurrentPage.current !== currentPage) {
      console.log(`Current page changed from ${stableCurrentPage.current} to ${currentPage}`);
      stableCurrentPage.current = currentPage;
      
      const pagesNeeded = currentPage;
      const pagesAvailable = billsQuery.data?.pages.length || 0;

      console.log(`Pages needed for current page: ${pagesNeeded}, pages available: ${pagesAvailable}`);
      
      if (pagesAvailable < pagesNeeded && billsQuery.hasNextPage && !billsQuery.isFetchingNextPage) {
        console.log(`Fetching more pages to support current page ${currentPage}`);
        billsQuery.fetchNextPage();
      }
    }
  }, [currentPage, billsQuery])

  // Get all bills first
  const allBills = useMemo(() => {
    return billsQuery.data?.pages.flatMap(page => page.items) || []
  }, [billsQuery.data])

  // Filter bills based on search
  const filteredBills = useMemo(() => {
    if (!searchQuery.trim()) return allBills;

    const query = searchQuery.trim().toUpperCase();
    return allBills.filter(bill => {
      const billNumber = bill.billNumber || '';
      return billNumber.startsWith(query);
    });
  }, [allBills, searchQuery])

  // Apply sorting to filtered bills
  const sortedBills = useMemo(() => {
    return sortBills(filteredBills)
  }, [filteredBills, sortBills])

  // Get bills for the current page
  const getCurrentPageBills = (): Bill[] => {
    const startIdx = (currentPage - 1) * pageSize
    const endIdx = startIdx + pageSize
    return sortedBills.slice(startIdx, endIdx)
  }

  const totalItems = sortedBills.length
  const totalPages = Math.ceil(totalItems / pageSize)

  // Ensure current page is valid after filtering
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      console.log(`Current page ${currentPage} is greater than totalPages ${totalPages}, setting to ${totalPages}`);
      setCurrentPage(totalPages);
    }
  }, [totalPages])

  useEffect(() => {
    if (billsQuery.hasNextPage && !billsQuery.isFetchingNextPage) {
      billsQuery.fetchNextPage();
    }
  }, [billsQuery.data, billsQuery.hasNextPage, billsQuery.isFetchingNextPage])

  const updateBillsWorkflowMutation = useMutation({
    mutationFn: async ({ billIds, newStatus }: { billIds: string[], newStatus: BillWorkflowStatus }) => {
      if (!selectedSession) throw new Error('No session selected')
      const promises = billIds.map(billId => 
        billsApi.updateBillWorkflow({
          session: selectedSession,
          billNumber: billId,
          workflowStatus: newStatus
        })
      )
      await Promise.all(promises)
      return billIds.length
    },
    onSuccess: (count, { newStatus }) => {
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bills', selectedSession, activeTab] }),
        queryClient.invalidateQueries({ queryKey: ['bills', selectedSession, newStatus] })
      ])
      setSelectedBills([])
      toast({
        title: "Success",
        description: `${count} bill${count === 1 ? '' : 's'} updated successfully`
      })
    },
    onError: (error, { billIds }) => {
      // Clear processing state on error
      billIds.forEach(billId => {
        setProcessingActions(prev => {
          const newState = { ...prev }
          delete newState[billId]
          return newState
        })
      })

      console.error('Failed to update bills:', error)
      toast({
        title: "Error",
        description: "Failed to update bill status",
        variant: "destructive"
      })
    }
  })

  const processBillActionMutation = useMutation({
    mutationFn: async ({ billIds, action }: { billIds: string[], action: BillAction }) => {
      if (!selectedSession) throw new Error('No session selected')
      const results = await Promise.all(
        billIds.map(billId =>
          billsApi.processBillAction({
            session: selectedSession,
            billNumber: billId,
            action: action
          })
        )
      )
      billIds.forEach(billId => {
        // Debug what's happening with the processing action
        console.log(`Setting processing action for ${billId} to ${action}`);
        setProcessingActions(prev => ({
          ...prev,
          [billId]: action
        }))
      })
      return {
        billIds,
        action,
        jobIds: results.map(r => r.job_id)
      }
    },
    onSuccess: ({ billIds, action, jobIds }) => {
      billIds.forEach((billId, index) => {
        const jobId = jobIds[index]
        if (!jobId) return

        // Add a delay before starting to poll to give the job time to be created
        setTimeout(() => {
          const intervalId = setInterval(async () => {
            try {
              // Use the app ID from env variable
              const jobStatus = await getJob(import.meta.env.VITE_APP_KEY, jobId)

              // If job is complete or errored, stop polling
              if (jobStatus.status === JobStatus.COMPLETED || jobStatus.status === JobStatus.ERROR) {
                clearInterval(intervalId)
                console.log(`Removing processing action for ${billId} - job completed or errored`);
                setProcessingActions(prev => {
                  const newState = { ...prev }
                  delete newState[billId]
                  return newState
                })

                if (jobStatus.status === JobStatus.COMPLETED) {
                  console.log(`Job ${jobId} completed`)
                  // Fetch the updated bill
                  const bill = await billsApi.getBill(selectedSession!, billId)

                  // Update the bill in the cache
                  queryClient.setQueryData(
                    ['bills', selectedSession, activeTab],
                    (oldData: any) => {
                      if (!oldData?.pages) return oldData
                      return {
                        ...oldData,
                        pages: oldData.pages.map((page: any) => ({
                          ...page,
                          items: page.items.map((item: Bill) =>
                            item.billNumber === billId ? bill : item
                          )
                        }))
                      }
                    }
                  )
                } else if (jobStatus.status === JobStatus.ERROR) {
                  toast({
                    title: "Processing Failed",
                    description: `Failed to process bill ${billId}`,
                    variant: "destructive"
                  })
                }
              }
            } catch (error) {
              console.error('Error polling for job status:', error)
            }
          }, 2000)

          // Clear interval after 5 minutes to prevent infinite polling
          setTimeout(() => {
            clearInterval(intervalId)
            console.log(`Removing processing action for ${billId} - timeout reached`);
            setProcessingActions(prev => {
              const newState = { ...prev }
              delete newState[billId]
              return newState
            })
          }, 300000)
        }, 3000); // Initial delay of 3 seconds before starting to poll
      })

      setSelectedBills([])
      toast({
        title: "Processing Started",
        description: `AI ${action} will be generated for ${billIds.length} bill${billIds.length === 1 ? '' : 's'}`
      })
    },
    onError: (error) => {
      console.error('Failed to process bill action:', error)
      toast({
        title: "Error",
        description: "Failed to start bill processing",
        variant: "destructive"
      })
    }
  })

  const handleSelectBill = useCallback((billId: string) => {
    setSelectedBills(prev => 
      prev.includes(billId) 
        ? prev.filter(id => id !== billId)
        : [...prev, billId]
    )
  }, [])

  const handleSelectAll = useCallback((bills: Bill[]) => {
    setSelectedBills(prev => 
      prev.length === bills.length 
        ? []
        : bills.map(b => b.billNumber)
    )
  }, [])

  const updateBillsWorkflow = async (billIds: string[], newStatus: BillWorkflowStatus) => {
    await updateBillsWorkflowMutation.mutateAsync({ billIds, newStatus })
    return billIds.length
  }

  const processBillAction = async (billIds: string[], action: BillAction) => {
    await processBillActionMutation.mutateAsync({ billIds, action })
    return billIds.length
  }

  const isLoading = billsQuery.isPending || !selectedSession

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }

  return {
    bills: getCurrentPageBills(),
    isLoading,
    isError: billsQuery.isError,
    error: billsQuery.isError ? 'Failed to load bills' : null,
    selectedBills,
    activeTab,
    selectedSession,
    setSelectedSession,
    setActiveTab,
    handleSelectBill,
    handleSelectAll,
    updateBillsWorkflow,
    processBillAction,
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    searchQuery,
    setSearchQuery,
    totalFilteredItems: totalItems,
    isFetchingNextPage: billsQuery.isFetchingNextPage,
    processingActions,
    sortField,
    sortDirection,
    setSorting
  }
}